import { Group, Text } from '@mantine/core';
import { IconAlertCircle, IconCheck, IconClockPause, IconEye } from '@tabler/icons-react';
import dayjs, { type Dayjs } from 'dayjs';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { tKeysLoose, useTranslation } from 'locales';
import { type Activity, EmailDeliveryStatus } from 'types/store/activity';

interface EmailDeliveryStatusCompactProps {
  statuses: NonNullable<Activity['email_statuses']>;
}

export const EmailDeliveryStatusCompact = ({ statuses }: EmailDeliveryStatusCompactProps) => {
  const { t } = useTranslation();
  const { status, timestamp } = getHighestPrecedenceStatus(statuses);
  const {
    package: { can_use_emails_activity: canUseEmailStatus },
  } = useCompanyConfiguration();

  if (!canUseEmailStatus) return null;

  return (
    <Group gap={4}>
      {ICON_FOR_STATUS[status]}
      <Text fw="bold" size="sm" c="purple">
        {t(tKeysLoose.MAILS.STATUS[status.toUpperCase()])}
      </Text>
      {status !== EmailDeliveryStatus.BOUNCE && (
        <Text size="xs" c="dimmed" ml={4}>
          • {timestamp?.format('LLL')}
        </Text>
      )}
    </Group>
  );
};

/**
 * Returns the highest precedence status and the corresponding timestamp.
 */
function getHighestPrecedenceStatus(statuses: NonNullable<Activity['email_statuses']>): {
  status: EmailDeliveryStatus;
  timestamp: Dayjs | null;
} {
  const availableKeys = Object.keys(statuses) as Array<EmailDeliveryStatus>;
  const sortedKeys = availableKeys.sort((a, b) => PRECEDENCE.indexOf(a) - PRECEDENCE.indexOf(b));
  const highestPrecedenceKey = sortedKeys[0];
  const correspondingDates = statuses[highestPrecedenceKey]!;
  const correspondingDatesParsed = correspondingDates.map(date => dayjs(date));

  return {
    status: highestPrecedenceKey,
    timestamp: dayjs.max(...correspondingDatesParsed),
  };
}

const PRECEDENCE = [
  EmailDeliveryStatus.BOUNCE,
  EmailDeliveryStatus.DROPPED,
  EmailDeliveryStatus.CLICK,
  EmailDeliveryStatus.OPEN,
  EmailDeliveryStatus.DELIVERED,
  EmailDeliveryStatus.PROCESSED,
  EmailDeliveryStatus.DEFERRED,
];

const ICON_PROPS = {
  size: 16,
  stroke: 3,
  color: 'var(--mantine-color-purple-6)',
};

const ICON_FOR_STATUS: Record<EmailDeliveryStatus, React.ReactNode> = {
  [EmailDeliveryStatus.PROCESSED]: <IconClockPause {...ICON_PROPS} />,
  [EmailDeliveryStatus.DEFERRED]: <IconClockPause {...ICON_PROPS} />,
  [EmailDeliveryStatus.DELIVERED]: <IconCheck {...ICON_PROPS} />,
  [EmailDeliveryStatus.OPEN]: <IconEye {...ICON_PROPS} />,
  [EmailDeliveryStatus.CLICK]: <IconCheck {...ICON_PROPS} />,
  [EmailDeliveryStatus.BOUNCE]: <IconAlertCircle {...ICON_PROPS} />,
  [EmailDeliveryStatus.DROPPED]: <IconAlertCircle {...ICON_PROPS} />,
};
